import {
  codePreference,
  codeProfil,
  coverBook,
  publicImgMajorTest,
  publicSvgPath,
} from '@orientaction/utils';
import { GoFurtherConfig, MajorTestKind, MajorTestResultConfig } from '../types';

const BASE_GO_FURTHER = {
  contactTitle: 'CONTACTEZ NOTRE SERVICE DE FORMATION',
  contactDescription: 'Pour une offre de formation personnalisée',
};

type MajorTestResultStaticConfig = Omit<MajorTestResultConfig, 'description'>;

export const MAJOR_TEST_CONFIGS: Record<MajorTestKind, MajorTestResultStaticConfig> = {
  meaning: {
    kind: 'meaning',
    title: 'Sens® - test des valeurs',
    breadcrumbLabel: 'Sens® - test des valeurs',
    heroImage: `${publicImgMajorTest}/major-test-result-human-sens.png`,
    historyRedirectPath: '/resultat-meaning-value',
    retakePath: '/tests/meaning-value',
    contactFormTest: { id: 1, label: 'Sens®, test des valeurs' },
    goFurther: {
      showShopCard: true,
      shopTitle: 'Acheter le jeu de cartes des valeurs',
      shopDescription:
        'Découvrez le jeu de cartes des valeurs.\nIl permet d’identifier ses valeurs profondes ou celles de ses proches.\n3 € de réduction avec le code PROMO3EUROS.',
      shopDescriptionSemiboldLines: [0, 2],
      shopImage: `${publicSvgPath}/jeu_cartes.png`,
      shopImageAlt: 'Jeu de cartes des valeurs',
      shopUrl: 'https://boutique-orientaction.com/products/jeu-de-cartes-des-valeurs',
      shopButtonLabel: 'Acheter le jeu',
      contactTitle: 'CONTACTEZ NOTRE SERVICE DE FORMATION',
      contactDescription:
        'Pour organiser un atelier ou une formation sur les valeurs dans votre entreprise',
    },
    detailsSectionTitle: 'DÉCOUVREZ VOS 3 VALEURS PRINCIPALES',
  },
  needs: {
    kind: 'needs',
    title: 'Needs® - test des 7 besoins professionnels et personnels',
    breadcrumbLabel: 'Needs® - test des besoins',
    heroImage: `${publicImgMajorTest}/major-test-result-human-needs.png`,
    historyRedirectPath: '/resultat-need',
    retakePath: '/tests/needs',
    contactFormTest: { id: 2, label: 'Needs®, test des besoins' },
    goFurther: BASE_GO_FURTHER,
    detailsSectionTitle: 'DÉCOUVREZ VOS 3 BESOINS PRINCIPAUX',
  },
  needsPro: {
    kind: 'needsPro',
    title: 'Needs® Pro - test des 7 besoins professionnels',
    breadcrumbLabel: 'Needs® Pro - test des besoins professionnels',
    heroImage: `${publicImgMajorTest}/major-test-result-human-needs-pro.png`,
    historyRedirectPath: '/resultat-need-pro',
    retakePath: '/tests/needs-pro',
    contactFormTest: { id: 3, label: 'Needs® pro, test des besoins professionnels' },
    goFurther: BASE_GO_FURTHER,
    detailsSectionTitle: 'DÉCOUVREZ VOS 3 BESOINS PRINCIPAUX',
  },
  preference: {
    kind: 'preference',
    title: 'Préférences® - test des 32 personnalités',
    breadcrumbLabel: 'Préférences® - test des personnalités',
    heroImage: `${publicImgMajorTest}/major-test-result-human-preferences-32.png`,
    historyRedirectPath: '/resultat-preference',
    retakePath: '/tests/preferences-personnalites',
    contactFormTest: { id: 4, label: 'Préférences®, test des personnalités' },
    goFurther: {
      showShopCard: true,
      shopTitle: 'Découvrez votre profil',
      shopDescription:
        'Découvrez les nuances fascinantes de votre personnalité en visualisant votre profil complet ! Il vous permettra de mieux vous comprendre, d’explorer vos forces et vos zones de développement, et d’ouvrir de nouvelles perspectives professionnelles et personnelles.',
      shopButtonLabel: 'Visualiser',
      shopDirectOpen: true,
      shopSecondaryLabel: 'Commander le document en version papier',
      shopImageAlt: 'Couverture du profil',
      ...BASE_GO_FURTHER,
    },
    detailsSectionTitle: 'DÉCOUVREZ VOS PRÉFÉRENCES',
  },
  harmony: {
    kind: 'harmony',
    title: 'Harmony® - test des 16 soft skills',
    breadcrumbLabel: 'Harmony® - test des soft skills',
    heroImage: `${publicImgMajorTest}/major-test-result-human-harmony.png`,
    historyRedirectPath: '/resultat-harmony',
    retakePath: '/tests/harmony',
    contactFormTest: { id: 5, label: 'Harmony®, le test des soft skills' },
    goFurther: {
      showShopCard: true,
      shopTitle: 'Acheter le livre',
      shopDescription:
        'Découvrez le livre "la méthode 16 soft skills". Il permet de développer vos soft skills dans les domaines pro et perso.',
      shopImage: `${publicSvgPath}/illustration_livre.png`,
      shopImageAlt: 'Livre la méthode 16 soft skills',
      shopUrl: 'https://orientaction.kneo.me/shop/view/868273',
      shopButtonLabel: 'Acheter le livre',
      ...BASE_GO_FURTHER,
    },
    detailsSectionTitle: 'EN SAVOIR + SUR LES SOFT SKILLS',
  },
  study: {
    kind: 'study',
    title: "Study® - test d'orientation",
    breadcrumbLabel: "Study® - test d'orientation",
    heroImage: `${publicImgMajorTest}/major-test-result-human-study.png`,
    historyRedirectPath: '/resultat-study',
    retakePath: '/tests/study',
    contactFormTest: { id: 6, label: "Study®, test d'orientation" },
    goFurther: BASE_GO_FURTHER,
    detailsSectionTitle: 'VOS RÉSULTATS STUDY®',
  },
  preferenceJeune: {
    kind: 'preferenceJeune',
    title: 'Préférences® Jeunes',
    breadcrumbLabel: 'Préférences® Jeunes',
    heroImage: `${publicImgMajorTest}/major-test-result-human-preferences-jeunes.png`,
    historyRedirectPath: '/res-pref-j',
    retakePath: '/tests/preferences-jeune',
    contactFormTest: { id: 7, label: 'Préférences® Jeunes' },
    goFurther: BASE_GO_FURTHER,
    detailsSectionTitle: 'DÉCOUVREZ VOS PRÉFÉRENCES',
  },
};

export const getMajorTestConfig = (kind: MajorTestKind): MajorTestResultStaticConfig =>
  MAJOR_TEST_CONFIGS[kind];

export const getPreferenceShopFields = (
  resultat: any[] | undefined,
  language: string,
): Partial<GoFurtherConfig> => {
  if (!resultat?.length) {
    return { showShopCard: false };
  }

  const code = [
    codePreference[resultat[0]?.category]?.[resultat[0]?.key],
    codePreference[resultat[1]?.category]?.[resultat[1]?.key],
    codePreference[resultat[2]?.category]?.[resultat[2]?.key],
    codePreference[resultat[4]?.category]?.[resultat[4]?.key],
    codePreference[resultat[3]?.category]?.[resultat[3]?.key],
  ].join('');

  const profile = codeProfil[code];

  if (!profile) {
    return { showShopCard: false };
  }

  return {
    showShopCard: true,
    shopImage: `${coverBook}/${language === 'GB' ? profile.imgEn : profile.img}`,
    shopUrl: language === 'GB' ? profile.linkVisEn : profile.linkVis,
    shopSecondaryUrl: profile.linkBuy,
  };
};
